import { PUBLIC_ROUTES } from '@config/publicEndpoints';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import Cookies from 'js-cookie';
import { refreshTokenService } from '../services/auth/authService';
import { useAuthStore } from '../services/auth/authStore';

// Global variable to prevent multiple refresh token calls
let refreshTokenPromise: Promise<any> | null = null;

export const API: AxiosInstance = axios.create({
	baseURL: process.env.REACT_APP_API_BASE_URL,
	timeout: 50000,
	headers: {
		'Content-Type': 'application/json',
	},
});

export const createApiUrl = (
	moduleName: string,
	otherPath?: string,
	tagVersion = process.env.REACT_APP_VERSION_TAG
): string => {
	if (!moduleName) {
		throw new Error('Both module name and other path are required');
	}
	const baseURL = API.defaults.baseURL;

	if (!baseURL) {
		throw new Error('Base URL is not defined in Axios instance.');
	}

	// Ensure moduleName doesn't have trailing slash
	const cleanModuleName = moduleName.endsWith('/') ? moduleName.slice(0, -1) : moduleName;
	
	// Ensure tagVersion doesn't have leading or trailing slashes
	const cleanTagVersion = tagVersion 
		? tagVersion.replace(/^\/+|\/+$/g, '') 
		: '';
	
	// Ensure otherPath doesn't have leading slash
	const cleanOtherPath = otherPath 
		? otherPath.startsWith('/') ? otherPath.slice(1) : otherPath
		: '';
	
	// Construct URL with proper slashes
	return `${baseURL}${cleanModuleName}/${cleanTagVersion}/${cleanOtherPath}`;
};

export const http = {
	get: async <T>(
		moduleName: string,
		otherPath = '',
		params?: object
	): Promise<AxiosResponse<T>> => {
		const url = createApiUrl(moduleName, otherPath);

		return API.get<T>(url, { params });
	},

	post: async <T>(
		moduleName: string,
		otherPath?: string,
		data?: object,
		config?: AxiosRequestConfig<any>
	): Promise<AxiosResponse<T>> => {
		const url = createApiUrl(moduleName, otherPath);

		return API.post<T>(url, data, config);
	},

	put: async <T>(
		moduleName: string,
		otherPath?: string,
		data?: object
	): Promise<AxiosResponse<T>> => {
		const url = createApiUrl(moduleName, otherPath);

		return API.put<T>(url, data);
	},

	patch: async <T>(
		moduleName: string,
		otherPath?: string,
		data?: object
	): Promise<AxiosResponse<T>> => {
		const url = createApiUrl(moduleName, otherPath);

		return API.patch<T>(url, data);
	},

	delete: async <T>(
		moduleName: string,
		otherPath?: string,
		params?: object
	): Promise<AxiosResponse<T>> => {
		const url = createApiUrl(moduleName, otherPath);

		return API.delete<T>(url, { params });
	},
};

API.interceptors.request.use((config) => {
	// Get current language from i18next
	const currentLanguage = localStorage.getItem('i18nextLng');

	// Add accept-language header
	config.headers['accept-language'] = currentLanguage || 'ka'; // Default to Georgian if no language set

	return config;
});

API.interceptors.request.use((config) => {
	const accessToken = Cookies.get('accessToken');
	const isPublicRoute = PUBLIC_ROUTES.some((route) => config.url?.includes(route));

	if (!isPublicRoute && accessToken) {
		config.headers.Authorization = `Bearer ${accessToken}`;
	}

	return config;
});

// @TODO implement refresh token logic when BE is ready
API.interceptors.response.use(
	(response) => response,

	async (error) => {
		const originalRequest = error.config;

		// Handle both 401 Unauthorized and 403 Forbidden with message "Forbidden"
		if (
			error.response?.status === 403 &&
			error.response?.data?.message === 'Forbidden' &&
			!originalRequest._retry
		) {
			originalRequest._retry = true;

			// If a refresh is already in progress, wait for it
			if (refreshTokenPromise) {
				try {
					await refreshTokenPromise;
					// After the refresh is complete, retry the original request with new token
					const newAccessToken = Cookies.get('accessToken');
					if (newAccessToken) {
						originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
						return API(originalRequest);
					}
				} catch (refreshError) {
					return Promise.reject(refreshError);
				}
			}

			const refreshToken = localStorage.getItem('refreshToken') || '';

			// Start the refresh process
			refreshTokenPromise = refreshTokenService(refreshToken)
				.then((data) => {
					// Update both cookie and auth store consistently
					Cookies.set('accessToken', data.accessToken, { secure: true });
					useAuthStore.getState().setAccessToken(data.accessToken, refreshToken);
					return data;
				})
				.catch((error) => {
					// Clear tokens and logout on refresh failure
					useAuthStore.getState().logout();
					Cookies.remove('accessToken');
					localStorage.removeItem('refreshToken');
					throw error;
				})
				.finally(() => {
					// Clear the promise when done
					refreshTokenPromise = null;
				});

			try {
				const data = await refreshTokenPromise;
				originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
				return API(originalRequest);
			} catch (refreshError) {
				return Promise.reject(refreshError);
			}
		}

		return Promise.reject(error);
	}
);

export interface ApiResponse<T> {
	data: T;
	success: boolean;
	error: string;
}

export interface ApiPaginatedResponse<T> extends ApiResponse<T> {
	hasNextPage: boolean;
	totalData: number;
	totalPages: number;
	currentPage: number;
}
